import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import { Colours } from "../assets/global/Theme-variable";
import { StyledTag } from "./StyledTag";
import { Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGridApiContext } from "@mui/x-data-grid";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";

const DraggableChip = ({ option, index, getTagProps, type, moveChip }) => {
  const { key, ...rest } = getTagProps({ index });
  const ref = React.useRef(null);

  const [, drop] = useDrop({
    accept: "DraggableTag",
    hover(item) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      moveChip(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: "DraggableTag",
    item: () => {
      return { key, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <Chip
      ref={ref}
      variant="filled"
      label={
        option?.policy_name
          ? option?.policy_name
          : type === "priceband"
          ? option?.priceband_name
          : option?.tag_name
      }
      size="medium"
      key={key}
      {...rest}
      onMouseDown={(e) => {
        // Prevent the grid from intercepting the mouse down so drag can start
        e.stopPropagation();
      }}
      deleteIcon={<CloseIcon size={10} />}
      sx={{
        cursor: "pointer",
        borderRadius: "6px",
        backgroundColor: Colours.gsblue,
        padding: "2px 4px 2px 8px",
        color: "white",
        borderColor: Colours.gsblue,
        fontSize: 10,
        "& svg": {
          fontSize: "14px",
          cursor: "pointer",
          color: "white !important",
        },
        "& .MuiChip-deleteIcon": {
          fontSize: "12px",
        },
        opacity,
      }}
    />
  );
};

export const StandardAutoComplete = (props) => {
  const [selectedOptions, setSelectedOptions] = React.useState(
    props.value || []
  );
  const [values, setValues] = React.useState(
    selectedOptions.map((val) => val && val?.policy_ref)
  );

  React.useEffect(() => {
    setValues(selectedOptions.map((val) => val && val?.policy_ref));
  }, [selectedOptions]);

  const { id, field, value, options, type } = props;
  const ref = useGridApiContext();
  React.useEffect(() => {
    if (value) {
      ref.current.setEditCellValue({
        id,
        field,
        value: values,
      });
    }
  }, [values]);

  const moveChip = React.useCallback((dragIndex, hoverIndex) => {
    setSelectedOptions((prev) =>
      update(prev, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prev[dragIndex]],
        ],
      })
    );
  }, []);

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <DndProvider backend={HTML5Backend}>
        <Autocomplete
          sx={{
            width: "100%",
            "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: "none",
              },
            "& .MuiInput-root::before .MuiInput-root::hover": {
              border: "none",
            },
          }}
          multiple
          variant="outlined"
          id="tags-standard"
          options={options}
          getOptionLabel={(option) =>
            option?.policy_name
              ? option?.policy_name
              : type === "priceband"
              ? option.priceband_name
              : option?.tag_name
          }
          getOptionKey={(option) => JSON.stringify(option)}
          isOptionEqualToValue={(option, value) => {
            if (option && option?.policy_ref && value && value?.policy_ref) {
              return option?.policy_ref === value?.policy_ref;
            }
            if (
              option &&
              option?.priceband_ref &&
              value &&
              value?.priceband_ref
            ) {
              return option?.priceband_ref === value?.priceband_ref;
            }
            return false;
          }}
          value={selectedOptions}
          renderTags={(value, getTagProps) =>
            value?.map((option, index) => (
              <DraggableChip
                key={JSON.stringify(option)}
                option={option}
                index={index}
                getTagProps={getTagProps}
                type={type}
                moveChip={moveChip}
              />
            ))
          }
          onChange={(e, newValue) => setSelectedOptions(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              InputProps={{ ...params.InputProps, disableUnderline: true }}
            />
          )}
        />
      </DndProvider>
    </Stack>
  );
};
